import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import AddressFieldsProps from '../proptypes/addressFields';
import useFocus from '../hooks/useFocus';
import style from '../style/components/AddressInputFields.module.scss';
import InputWithError from './InputWithError';
import LabelSelect from './LabelSelect';
import SmartyStreetsAutocomplete from './SmartyStreetsAutocomplete';
import { fetchStates } from '../actions';

function defaultFormData(initialFormData) {
  return {
    city: initialFormData.city || '',
    state: initialFormData.state || '',
    street: initialFormData.street || '',
    street2: initialFormData.street2 || '',
    zip: initialFormData.zip || '',
  };
}

const AddressInputFields = ({
  disabled = false,
  errors = {},
  initialFormData = {},
  onChange,
}) => {
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state.states);
  const [formData, setFormData] = useState(defaultFormData(initialFormData));
  const [zipInputRef, setZipInputFocus] = useFocus();

  const stateOptions = useMemo(
    () =>
      stateData.states.map((s) => ({
        label: s.name,
        value: s.code,
      })),
    [stateData.states],
  );

  useEffect(() => {
    if (!stateData.error && !stateData.isFetching && !stateData.states.length) {
      dispatch(fetchStates());
    }
  }, [dispatch, stateData]);

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      const changedField = { [name]: value };

      setFormData((formData) => ({ ...formData, ...changedField }));
      onChange && onChange(changedField);
    },
    [onChange, setFormData],
  );

  const handleSelectSuggestion = useCallback(
    (selectedAddressInfo) => {
      // update form with selected suggestion
      setFormData(selectedAddressInfo);
      onChange && onChange(selectedAddressInfo);

      // focus on zip field which is the last one filled out
      setZipInputFocus();
    },
    [onChange, setZipInputFocus],
  );

  return (
    <>
      <div className={style.inputGroup}>
        <div className={style.fullWidth}>
          <SmartyStreetsAutocomplete
            name="street"
            data-testid="address-input-street-input"
            label="Address Line 1"
            placeholder="Enter street address"
            error={errors.street}
            value={formData.street}
            noMargin
            required
            handleChange={handleChange}
            handleSelectSuggestion={handleSelectSuggestion}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={style.inputGroup}>
        <div className={style.fullWidth}>
          <InputWithError
            name="street2"
            data-testid="address-input-street2-input"
            label="Address Line 2"
            placeholder="Enter apt, suite, unit, building"
            error={errors.street2}
            value={formData.street2}
            onChange={handleChange}
            noMargin
            disabled={disabled}
          />
        </div>
      </div>

      <div className={style.inputGroup}>
        <div className={style.fullWidth}>
          <InputWithError
            name="city"
            data-testid="address-input-city-input"
            label="City"
            placeholder="Enter city name"
            error={errors.city}
            value={formData.city}
            onChange={handleChange}
            noMargin
            required
            disabled={disabled}
          />
        </div>
      </div>

      <div className={style.inputGroup}>
        <div className={style.halfWidth}>
          <LabelSelect
            name="state"
            data-testid="address-input-state-input"
            label="State"
            placeholder="Select a state"
            error={errors.state}
            value={formData.state}
            onChange={handleChange}
            options={stateOptions}
            noMargin
            required
            disabled={disabled}
          />
        </div>
        <div className={style.halfWidth}>
          <InputWithError
            name="zip"
            data-testid="address-input-zip-input"
            label="ZIP Code"
            placeholder="Enter ZIP code"
            error={errors.zip}
            value={formData.zip}
            onChange={handleChange}
            inputRef={zipInputRef}
            noMargin
            required
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
};

AddressInputFields.prototype = {
  disabled: PropTypes.bool,
  errors: AddressFieldsProps,
  initialFormData: AddressFieldsProps,
  onChange: PropTypes.func,
};

export default AddressInputFields;
